import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

// Import modular components
import {
  UserFilters,
  BulkActionsBar,
  UserTable,
  Pagination,
  UserDetailModal,
  GrantCreditsModal,
  DeductCreditsModal,
  ChangePlanModal,
  RefundModal,
  DeleteUserModal,
  ImpersonateModal,
  EnableUserModal,
} from "@/components/admin/users";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkGrantModal, setShowBulkGrantModal] = useState(false);
  const [showBulkDeductModal, setShowBulkDeductModal] = useState(false);
  const [showBulkPlanModal, setShowBulkPlanModal] = useState(false);

  // Impersonation state
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState("");
  const [showEnableModal, setShowEnableModal] = useState(false);

  // Form states
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [deductAmount, setDeductAmount] = useState("");
  const [deductReason, setDeductReason] = useState("");
  const [newPlan, setNewPlan] = useState("starter");
  const [newCycle, setNewCycle] = useState("monthly");
  const [planReason, setPlanReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundType, setRefundType] = useState("other");

  useEffect(() => {
    fetchUsers();
  }, [page, search, planFilter, statusFilter]);

  const fetchUsers = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      if (planFilter) params.append("plan_type", planFilter);
      if (statusFilter) params.append("subscription_status", statusFilter);

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error("Failed to load users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedUser(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  // Action Handlers
  const handleGrantCredits = async () => {
    if (!grantAmount || !grantReason) {
      toast.error("Please fill all fields");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/grant-credits`,
        { amount: parseInt(grantAmount), reason: grantReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Granted ${grantAmount} credits`);
      setShowGrantModal(false);
      setGrantAmount("");
      setGrantReason("");
      fetchUserDetail(selectedUser.user.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant credits");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeductCredits = async () => {
    if (!deductAmount || !deductReason) {
      toast.error("Please fill all fields");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/deduct-credits`,
        { amount: parseInt(deductAmount), reason: deductReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deducted ${deductAmount} credits`);
      setShowDeductModal(false);
      setDeductAmount("");
      setDeductReason("");
      fetchUserDetail(selectedUser.user.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deduct credits");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!planReason) {
      toast.error("Please provide a reason");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/change-plan`,
        { plan_type: newPlan, billing_cycle: newCycle, reason: planReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan changed to ${newPlan}`);
      setShowPlanModal(false);
      setPlanReason("");
      fetchUserDetail(selectedUser.user.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully");
      setShowDeleteModal(false);
      setShowDetail(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleIssueRefund = async () => {
    if (!refundAmount || !refundReason) {
      toast.error("Please fill all fields");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/issue-refund`,
        { amount: parseInt(refundAmount), reason: refundReason, refund_type: refundType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Refunded ${refundAmount} credits`);
      setShowRefundModal(false);
      setRefundAmount("");
      setRefundReason("");
      setRefundType("other");
      fetchUserDetail(selectedUser.user.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to issue refund");
    } finally {
      setActionLoading(false);
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateReason.trim()) {
      toast.error("Please provide a reason for impersonation");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/impersonate`,
        { reason: impersonateReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem("original_admin_token", token);
      localStorage.setItem("original_admin_user", localStorage.getItem("admin_user"));
      localStorage.setItem("token", response.data.impersonation_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("impersonating", "true");
      localStorage.setItem("impersonation_session_id", response.data.session_id);
      toast.success(`Now impersonating ${response.data.user.email}. All actions are logged.`);
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start impersonation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnableUser = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/enable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User account enabled");
      setShowEnableModal(false);
      fetchUserDetail(selectedUser.user.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enable user");
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk action handlers
  const handleSelectAll = (e) => {
    setSelectedUserIds(e.target.checked ? users.map(u => u.id) : []);
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkDelete = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await Promise.all(
        selectedUserIds.map(userId =>
          axios.delete(`${API}/admin/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`Successfully deleted ${selectedUserIds.length} users`);
      setShowBulkDeleteModal(false);
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete users");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkGrant = async () => {
    if (!grantAmount || !grantReason) {
      toast.error("Please fill all fields");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await Promise.all(
        selectedUserIds.map(userId =>
          axios.post(
            `${API}/admin/users/${userId}/grant-credits`,
            { amount: parseInt(grantAmount), reason: grantReason },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success(`Granted ${grantAmount} credits to ${selectedUserIds.length} users`);
      setShowBulkGrantModal(false);
      setGrantAmount("");
      setGrantReason("");
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant credits");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDeduct = async () => {
    if (!deductAmount || !deductReason) {
      toast.error("Please fill all fields");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await Promise.all(
        selectedUserIds.map(userId =>
          axios.post(
            `${API}/admin/users/${userId}/deduct-credits`,
            { amount: parseInt(deductAmount), reason: deductReason },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success(`Deducted ${deductAmount} credits from ${selectedUserIds.length} users`);
      setShowBulkDeductModal(false);
      setDeductAmount("");
      setDeductReason("");
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deduct credits");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkChangePlan = async () => {
    if (!planReason) {
      toast.error("Please provide a reason");
      return;
    }
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await Promise.all(
        selectedUserIds.map(userId =>
          axios.post(
            `${API}/admin/users/${userId}/change-plan`,
            { plan_type: newPlan, billing_cycle: newCycle, reason: planReason },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success(`Changed ${selectedUserIds.length} users to ${newPlan} plan`);
      setShowBulkPlanModal(false);
      setPlanReason("");
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change plans");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Users</h1>
        <p className="text-muted-foreground mt-1">Manage user accounts and subscriptions</p>
      </div>

      {/* Filters */}
      <UserFilters
        search={search}
        setSearch={setSearch}
        planFilter={planFilter}
        setPlanFilter={setPlanFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {/* Bulk Actions */}
      <BulkActionsBar
        selectedCount={selectedUserIds.length}
        onGrantCredits={() => setShowBulkGrantModal(true)}
        onDeductCredits={() => setShowBulkDeductModal(true)}
        onChangePlan={() => setShowBulkPlanModal(true)}
        onDeleteAll={() => setShowBulkDeleteModal(true)}
        onClearSelection={() => setSelectedUserIds([])}
      />

      {/* Users Table */}
      <UserTable
        users={users}
        loading={loading}
        selectedUserIds={selectedUserIds}
        onSelectAll={handleSelectAll}
        onSelectUser={handleSelectUser}
        onViewDetails={fetchUserDetail}
      />

      {/* Pagination */}
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {/* User Detail Modal */}
      <UserDetailModal
        show={showDetail}
        onClose={() => setShowDetail(false)}
        selectedUser={selectedUser}
        onShowGrantModal={() => setShowGrantModal(true)}
        onShowRefundModal={() => setShowRefundModal(true)}
        onShowDeductModal={() => setShowDeductModal(true)}
        onShowPlanModal={() => setShowPlanModal(true)}
        onShowImpersonateModal={() => setShowImpersonateModal(true)}
        onShowDeleteModal={() => setShowDeleteModal(true)}
        onShowEnableModal={() => setShowEnableModal(true)}
      />

      {/* Individual Modals */}
      <GrantCreditsModal
        show={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        grantAmount={grantAmount}
        setGrantAmount={setGrantAmount}
        grantReason={grantReason}
        setGrantReason={setGrantReason}
        onGrant={handleGrantCredits}
        loading={actionLoading}
      />

      <DeductCreditsModal
        show={showDeductModal}
        onClose={() => setShowDeductModal(false)}
        deductAmount={deductAmount}
        setDeductAmount={setDeductAmount}
        deductReason={deductReason}
        setDeductReason={setDeductReason}
        onDeduct={handleDeductCredits}
        loading={actionLoading}
      />

      <ChangePlanModal
        show={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        newPlan={newPlan}
        setNewPlan={setNewPlan}
        newCycle={newCycle}
        setNewCycle={setNewCycle}
        planReason={planReason}
        setPlanReason={setPlanReason}
        onChangePlan={handleChangePlan}
        loading={actionLoading}
      />

      <RefundModal
        show={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        refundAmount={refundAmount}
        setRefundAmount={setRefundAmount}
        refundReason={refundReason}
        setRefundReason={setRefundReason}
        refundType={refundType}
        setRefundType={setRefundType}
        onRefund={handleIssueRefund}
        loading={actionLoading}
      />

      <DeleteUserModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        user={selectedUser?.user}
        onDelete={handleDeleteUser}
        loading={actionLoading}
      />

      <ImpersonateModal
        show={showImpersonateModal}
        onClose={() => setShowImpersonateModal(false)}
        user={selectedUser?.user}
        impersonateReason={impersonateReason}
        setImpersonateReason={setImpersonateReason}
        onImpersonate={handleImpersonate}
        loading={actionLoading}
      />

      <EnableUserModal
        show={showEnableModal}
        onClose={() => setShowEnableModal(false)}
        user={selectedUser?.user}
        onEnable={handleEnableUser}
        loading={actionLoading}
      />

      {/* Bulk Modals */}
      <GrantCreditsModal
        show={showBulkGrantModal}
        onClose={() => setShowBulkGrantModal(false)}
        grantAmount={grantAmount}
        setGrantAmount={setGrantAmount}
        grantReason={grantReason}
        setGrantReason={setGrantReason}
        onGrant={handleBulkGrant}
        loading={actionLoading}
        isBulk={true}
        selectedCount={selectedUserIds.length}
      />

      <DeductCreditsModal
        show={showBulkDeductModal}
        onClose={() => setShowBulkDeductModal(false)}
        deductAmount={deductAmount}
        setDeductAmount={setDeductAmount}
        deductReason={deductReason}
        setDeductReason={setDeductReason}
        onDeduct={handleBulkDeduct}
        loading={actionLoading}
        isBulk={true}
        selectedCount={selectedUserIds.length}
      />

      <ChangePlanModal
        show={showBulkPlanModal}
        onClose={() => setShowBulkPlanModal(false)}
        newPlan={newPlan}
        setNewPlan={setNewPlan}
        newCycle={newCycle}
        setNewCycle={setNewCycle}
        planReason={planReason}
        setPlanReason={setPlanReason}
        onChangePlan={handleBulkChangePlan}
        loading={actionLoading}
        isBulk={true}
        selectedCount={selectedUserIds.length}
      />

      <DeleteUserModal
        show={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onDelete={handleBulkDelete}
        loading={actionLoading}
        isBulk={true}
        selectedCount={selectedUserIds.length}
      />
    </div>
  );
}
